// eslint-disable-next-line boundaries/external

import type { MetaDescriptor } from "react-router";
import { parsePath } from "ufo";

import type { GetSettingsQuery, Seo_Translations } from "~/core";

import type { Pages } from "../types";

export function prepareSeoData(
  params: {
    pageSeo: Pages["seo"];
    fallbackSeo?: Pages["seo"];
    originalUrl: string;
    currentLang: string;
    languages: GetSettingsQuery["languages"];
  },
  override?: Partial<Seo_Translations>,
): Array<MetaDescriptor> {
  const { pageSeo, fallbackSeo, originalUrl } = params;
  const fallback = fallbackSeo?.translations?.[0];

  const {
    title = fallback?.title,
    description = fallback?.description,
    ogDescription = fallback?.ogDescription,
    ogTitle = fallback?.ogTitle,
    ogImage = fallback?.ogImage,
    ogUrl = fallback?.ogUrl,
    robots = fallback?.robots,
    canonical = fallback?.canonical,
    twitter_card = fallback?.twitter_card,
    twitter_description = fallback?.twitter_description,
    twitter_image = fallback?.twitter_image,
    twitter_title = fallback?.twitter_title,
    jsonld = fallback?.jsonld,
  } = pageSeo?.translations?.[0] ?? {};

  const alternate_links =
    pageSeo?.translations?.[0]?.seo_alternate_links?.items ??
    fallback?.seo_alternate_links?.items ??
    [];

  const alternateLinks = alternate_links?.map((item) => {
    return {
      hreflang: item?.alternate_link_id?.hreflang,
      href: item?.alternate_link_id?.href,
      tagName: "link",
      rel: "alternate",
    };
  });

  const result: Array<MetaDescriptor> = [
    { title: override?.title ?? title },

    { name: "description", content: override?.description ?? description },
    { property: "og:title", content: override?.ogTitle ?? ogTitle },
    {
      property: "og:description",
      content: override?.ogDescription ?? ogDescription,
    },
    { property: "og:image", content: override?.ogImage ?? ogImage },
    { property: "og:url", content: override?.ogUrl ?? ogUrl },
    { property: "robots", content: override?.robots ?? robots },
    { name: "twitter:card", content: override?.twitter_card ?? twitter_card },
    {
      name: "twitter:description",
      content: override?.twitter_description ?? twitter_description,
    },
    {
      name: "twitter:image",
      content: override?.twitter_image ?? twitter_image,
    },
    {
      name: "twitter:title",
      content: override?.twitter_title ?? twitter_title,
    },
    {
      tagName: "link",
      rel: "canonical",
      href: parsePath(override?.canonical ?? canonical ?? originalUrl).pathname,
    },
    ...alternateLinks,
  ];

  if (override?.jsonld ?? jsonld) {
    result.push({
      "script:ld+json": override?.jsonld ?? jsonld,
    });
  }
  return result;
}
